import {Distance} from '../Domain/Distance';
import {Force} from '../Domain/Force';
import {Curve} from '../Models/Curve';

export interface ReferenceCurve {
    curve: Curve | null;
}

function calculateA(force: Force, maxX: number): number {
    return (-4 * force.newton) / Math.pow(maxX, 2.0);
}

function calculateB(force: Force, maxX: number): number {
    const a = calculateA(force, maxX);
    return (force.newton - a * Math.pow(maxX / 2, 2)) / (maxX / 2);
}

function calculateY(a: number, b: number, x: number): number {
    return a * Math.pow(x, 2.0) + b * x;
}

function calculateParabola(
    a: number,
    b: number,
    x: number,
    peakForcePosition: Distance,
    strokeLength: Distance,
): number {
    // Calculate a new x-value
    let xnorm = x;
    if (x <= peakForcePosition.centimeters) {
        xnorm = (x / peakForcePosition.centimeters) * (strokeLength.centimeters / 2);
    } else {
        xnorm =
            ((x - peakForcePosition.centimeters) / (strokeLength.centimeters - peakForcePosition.centimeters)) *
                (strokeLength.centimeters / 2) +
            strokeLength.centimeters / 2;
    }
    return calculateY(a, b, xnorm);
}

export function calculateReferenceCurve(
    strokeLength: Distance | null,
    peakForce: Force | null,
    peakForcePosition: Distance | null,
    curve: Curve | null,
): ReferenceCurve {
    if (curve === null || strokeLength === null || peakForce === null || peakForcePosition === null) {
        // Handle the case when curve is null, you can return null or provide a default value.
        return {curve: null};
    }

    const a = calculateA(peakForce, strokeLength.centimeters);
    const b = calculateB(peakForce, strokeLength.centimeters);

    if (curve === undefined) {
        // Handle the case when curve is undefined.
        return {curve: null};
    }
    const refCurve = curve
        .map((point) => {
            const x = point.x;
            const y = calculateParabola(a, b, x, peakForcePosition, strokeLength);

            // Only add values to the new curve if y is above 0
            if (y >= 0) {
                return {x, y};
            } else {
                // Return null if y is not above 0
                return null;
            }
        })
        .filter((point) => point !== null) as Curve;

    return {curve: refCurve};
}
